import React, { useState } from 'react';
import { Settings, Server, Play, Check, Wifi } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import Badge from './Badge';
import Loading from './Loading';
import Alert from './Alert';
import { cameraService } from '../../services';
import type { EdgeProvisionResponse, Location } from '../../types';

interface EdgeProvisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
  location?: Location;
  onProvisionComplete?: () => void;
}

type ProvisionMode = 'auto' | 'simple';

const EdgeProvisionModal: React.FC<EdgeProvisionModalProps> = ({
  isOpen,
  onClose,
  locationId,
  locationName,
  location,
  onProvisionComplete,
}) => {
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provisionResult, setProvisionResult] = useState<EdgeProvisionResponse | null>(null);
  const [mode, setMode] = useState<ProvisionMode>('auto');
  
  // Simple provision settings
  const [channelInput, setChannelInput] = useState('1,2,3,4,5,6,7,8');
  const [cameraNamesInput, setCameraNamesInput] = useState('');
  
  // Common settings
  const [includeSubstreams, setIncludeSubstreams] = useState(true);
  const [forceTcp, setForceTcp] = useState(true);
  const [useExternal, setUseExternal] = useState(false);

  const handleProvision = async () => {
    try {
      setIsProvisioning(true);
      setError(null);

      let response: EdgeProvisionResponse;

      if (mode === 'auto') {
        // SaaS backend handles forwarding to edge device
        // NVR credentials are pulled from location config on the backend
        console.log('🚀 Auto Provision - Calling SaaS backend (will forward to edge device)');
        response = await cameraService.provisionEdgeCameras(locationId, {
          include_substreams: includeSubstreams,
          force_tcp: forceTcp,
          use_external: useExternal,
        });
      } else {
        // Parse channels from input (e.g., "1,2,3,4,5,6,7,8" or "1-8")
        let channels: number[] = [];
        const parts = channelInput.split(',').map(p => p.trim());
        for (const part of parts) {
          if (part.includes('-')) {
            const [start, end] = part.split('-').map(n => parseInt(n.trim()));
            for (let i = start; i <= end; i++) {
              channels.push(i);
            }
          } else {
            channels.push(parseInt(part));
          }
        }
        // Deduplicate and sort channels\n        const uniqueChannels: number[] = [];\n        for (const ch of channels) {\n          if (!isNaN(ch) && !uniqueChannels.includes(ch)) {\n            uniqueChannels.push(ch);\n          }\n        }\n        channels = uniqueChannels.sort((a, b) => a - b);

        // Parse camera names (e.g., "1:Front Door,2:Backyard")
        const cameraNames: Record<string, string> = {};
        if (cameraNamesInput.trim()) {
          const nameParts = cameraNamesInput.split(',').map(p => p.trim());
          for (const namePart of nameParts) {
            const [channel, name] = namePart.split(':').map(p => p.trim());
            if (channel && name) {
              cameraNames[channel] = name;
            }
          }
        }

        console.log('🚀 Simple Provision - Using manual channel list:', channels);
        response = await cameraService.provisionEdgeCamerasSimple(locationId, channels, {
          camera_names: Object.keys(cameraNames).length > 0 ? cameraNames : undefined,
          include_substreams: includeSubstreams,
          force_tcp: forceTcp,
        });
      }

      setProvisionResult(response);
      if (response.success && onProvisionComplete) {
        onProvisionComplete();
      }
    } catch (err) {
      console.error('❌ Provision Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to provision cameras');
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleClose = () => {
    if (!isProvisioning) {
      setProvisionResult(null);
      setError(null);
      onClose();
    }
  };

  const renderForm = () => (
    <div className="space-y-4">
      {/* Mode Selection */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setMode('auto')}
          className={`p-4 rounded-lg border-2 transition-colors text-left ${
            mode === 'auto'
              ? 'border-primary-500 bg-primary-500/10'
              : 'border-border-primary hover:border-primary-300'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Wifi size={20} className={mode === 'auto' ? 'text-primary-500' : 'text-text-muted'} />
            <span className="font-medium text-text-primary">Auto Discovery</span>
          </div>
          <p className="text-xs text-text-muted">
            Uses ISAPI to discover cameras from NVR automatically
          </p>
        </button>
        <button
          onClick={() => setMode('simple')}
          className={`p-4 rounded-lg border-2 transition-colors text-left ${
            mode === 'simple'
              ? 'border-primary-500 bg-primary-500/10'
              : 'border-border-primary hover:border-primary-300'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Settings size={20} className={mode === 'simple' ? 'text-primary-500' : 'text-text-muted'} />
            <span className="font-medium text-text-primary">Manual Channels</span>
          </div>
          <p className="text-xs text-text-muted">
            Specify channel numbers manually without ISAPI
          </p>
        </button>
      </div>

      {/* Auto Discovery Form */}
      {mode === 'auto' && (
        <div className="space-y-3 p-4 bg-surface-secondary rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Server size={16} className="text-primary-500" />
            <h4 className="text-sm font-medium text-text-primary">Auto Discovery</h4>
          </div>
          <p className="text-sm text-text-secondary">
            The edge device will connect to the NVR using credentials stored in the location configuration
            and automatically discover all connected cameras via ISAPI.
          </p>
          {location?.nvr_ip && (
            <div className="text-xs text-text-muted bg-surface-primary p-2 rounded space-y-1">
              <div>
                <span className="font-medium">NVR:</span> {location.nvr_ip}:{location.nvr_port || 80}
                {location.nvr_internal_port && (
                  <span className="text-text-muted"> (internal: {location.nvr_internal_port})</span>
                )}
                {location.nvr_username && <span> ({location.nvr_username})</span>}
              </div>
              {(location.nvr_rtsp_port || location.nvr_internal_rtsp_port) && (
                <div>
                  <span className="font-medium">RTSP:</span> {location.nvr_rtsp_port || 554}
                  {location.nvr_internal_rtsp_port && (
                    <span className="text-text-muted"> (internal: {location.nvr_internal_rtsp_port})</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Simple/Manual Form */}
      {mode === 'simple' && (
        <div className="space-y-3 p-4 bg-surface-secondary rounded-lg">
          <h4 className="text-sm font-medium text-text-primary mb-3">Channel Configuration</h4>
          <Input
            label="Channels"
            placeholder="1,2,3,4,5,6,7,8 or 1-8"
            value={channelInput}
            onChange={e => setChannelInput(e.target.value)}
            helperText="Comma-separated list or range (e.g., 1-8)"
          />
          <Input
            label="Camera Names (Optional)"
            placeholder="1:Front Door,2:Backyard,3:Parking"
            value={cameraNamesInput}
            onChange={e => setCameraNamesInput(e.target.value)}
            helperText="Format: channel:name,channel:name"
          />
        </div>
      )}

      {/* Common Options */}
      <div className="space-y-2 p-4 bg-surface-secondary rounded-lg">
        <h4 className="text-sm font-medium text-text-primary mb-3">Stream Options</h4>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeSubstreams"
            checked={includeSubstreams}
            onChange={e => setIncludeSubstreams(e.target.checked)}
            className="w-4 h-4 text-primary-500 border-border-primary rounded focus:ring-primary-500"
          />
          <label htmlFor="includeSubstreams" className="text-sm text-text-primary">
            Include substreams (lower quality for bandwidth savings)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="forceTcp"
            checked={forceTcp}
            onChange={e => setForceTcp(e.target.checked)}
            className="w-4 h-4 text-primary-500 border-border-primary rounded focus:ring-primary-500"
          />
          <label htmlFor="forceTcp" className="text-sm text-text-primary">
            Force TCP (more reliable, recommended)
          </label>
        </div>
        {mode === 'auto' && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useExternal"
              checked={useExternal}
              onChange={e => setUseExternal(e.target.checked)}
              className="w-4 h-4 text-primary-500 border-border-primary rounded focus:ring-primary-500"
            />
            <label htmlFor="useExternal" className="text-sm text-text-primary">
              Use external hostname (DDNS) instead of internal IP
            </label>
          </div>
        )}
      </div>
    </div>
  );

  const renderResult = () => (
    <div className="space-y-4">
      {/* Success Banner */}
      {provisionResult?.success && (
        <div className="flex items-center gap-3 p-4 bg-status-success/10 rounded-lg border border-status-success/20">
          <div className="w-10 h-10 rounded-full bg-status-success/20 flex items-center justify-center">
            <Check size={20} className="text-status-success" />
          </div>
          <div>
            <div className="font-medium text-status-success">Provisioning Complete!</div>
            <div className="text-sm text-text-muted">{provisionResult.message}</div>
          </div>
        </div>
      )}

      {/* Edge Device Info */}
      <div className="flex items-center gap-3 p-3 bg-surface-secondary rounded-lg">
        <Server size={20} className="text-primary-500" />
        <div>
          <div className="text-sm font-medium text-text-primary">Edge Device</div>
          <div className="text-xs text-text-muted">{provisionResult?.edge_device}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-lg font-bold text-text-primary">{provisionResult?.camera_count}</div>
          <div className="text-xs text-text-muted">cameras provisioned</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-status-success/10 rounded-lg text-center">
          <div className="text-2xl font-bold text-status-success">{provisionResult?.db_created || 0}</div>
          <div className="text-xs text-text-muted">Created in DB</div>
        </div>
        <div className="p-3 bg-primary-500/10 rounded-lg text-center">
          <div className="text-2xl font-bold text-primary-500">{provisionResult?.db_updated || 0}</div>
          <div className="text-xs text-text-muted">Updated</div>
        </div>
        <div className="p-3 bg-surface-secondary rounded-lg text-center">
          <div className="text-2xl font-bold text-text-muted">{provisionResult?.db_skipped || 0}</div>
          <div className="text-xs text-text-muted">Skipped</div>
        </div>
      </div>

      {/* go2rtc Restart Status */}
      {provisionResult?.go2rtc_restarted && (
        <div className="flex items-center gap-2 p-2 bg-primary-500/10 rounded-lg">
          <Check size={16} className="text-primary-500" />
          <span className="text-sm text-primary-500">go2rtc service restarted with new configuration</span>
        </div>
      )}

      {/* Camera List */}
      <div className="max-h-48 overflow-y-auto border border-border-primary rounded-lg">
        <table className="w-full">
          <thead className="bg-surface-secondary sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Channel</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Name</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {provisionResult?.cameras.map((camera) => (
              <tr key={camera.id} className="border-t border-border-primary">
                <td className="px-3 py-2 text-sm text-text-primary">Ch {camera.channel}</td>
                <td className="px-3 py-2 text-sm text-text-primary">{camera.name}</td>
                <td className="px-3 py-2">
                  {camera.enabled ? (
                    <Badge variant="success" size="sm" dot>Enabled</Badge>
                  ) : (
                    <Badge variant="default" size="sm">Disabled</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const footer = provisionResult ? (
    <div className="flex justify-end">
      <Button variant="primary" onClick={handleClose}>
        Done
      </Button>
    </div>
  ) : (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" onClick={handleClose} disabled={isProvisioning}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleProvision}
        leftIcon={<Play size={16} />}
        disabled={isProvisioning}
        isLoading={isProvisioning}
      >
        Provision Cameras
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Provision Edge Cameras - ${locationName}`}
      size="lg"
      closeOnOverlayClick={!isProvisioning}
      footer={footer}
    >
      {error && (
        <Alert
          variant="error"
          message={error}
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {isProvisioning && (
        <div className="py-12">
          <Loading size="lg" text="Provisioning cameras on edge device..." />
        </div>
      )}

      {!isProvisioning && !provisionResult && renderForm()}
      {provisionResult && renderResult()}
    </Modal>
  );
};

export default EdgeProvisionModal;
